<div class="agent-test-container">
  <!-- Loading -->
  @if (isLoadingAgent) {
    <div class="loading-container">
      <p-progressSpinner class="loading-spinner"></p-progressSpinner>
      <p i18n="@@agentEdit.loading">Loading agent data...</p>
    </div>
  }

  <!-- Error -->
  @if (loadAgentError && !isLoadingAgent) {
    <div class="error-container">
      <i class="pi pi-exclamation-circle error-icon"></i>
      <h3 i18n="@@agentTest.loadError.title">Failed to load agent</h3>
      <p i18n="@@agentTest.loadError.message">Unable to load agent data. Please check the link and try again.</p>
    </div>
  }

  @if ((agent || publicAgent) && !isLoadingAgent && !loadAgentError) {
    <div class="widget-frame" [ngClass]="placementClass()">
    <div class="tg-card"
         [class.is-compact]="bodyCollapsed"
         [ngClass]="[appearanceClass(), cardThemeClass()]">

      <!-- ============================ HEADER ============================ -->
      <div class="tg-header">
        <div class="tg-header-left">
          <div class="tg-avatar"
               [class.tg-avatar--with-status]="isTinyAppearance()"
               [pTooltip]="isTinyAppearance() ? avatarTooltip : undefined"
               tooltipPosition="bottom"
              tooltipStyleClass="widget-tooltip"
               [attr.aria-label]="isTinyAppearance() ? avatarTooltip : null"
               [attr.aria-hidden]="isTinyAppearance() ? null : true">
            <i class="pi pi-microchip"></i>
            @if (isTinyAppearance()) {
              <span class="tg-avatar-status-dot"
                    [class.status-dot--connected]="sessionStatus === 'connected'"
                    [class.status-dot--waiting]="sessionStatus === 'waiting'"
                    [class.status-dot--off]="sessionStatus === 'none' || sessionStatus === 'disconnected'">
              </span>
            }
          </div>
          @if (!isTinyAppearance()) {
            <div class="tg-header-text">
              <span class="tg-name">{{ agentName }}</span>
              @if (isCompactStatus()) {
                <span class="status-dot"
                      [class.status-dot--connected]="sessionStatus === 'connected'"
                      [class.status-dot--waiting]="sessionStatus === 'waiting'"
                      [class.status-dot--off]="sessionStatus === 'none' || sessionStatus === 'disconnected'"
                      [pTooltip]="statusText"
                      tooltipPosition="bottom"
              tooltipStyleClass="widget-tooltip">
                </span>
              } @else {
                <p-tag class="tg-status-tag"
                       [value]="statusText"
                       [severity]="getStatusSeverity()"
                       [icon]="sessionStatus === 'waiting' ? 'pi pi-spin pi-spinner' : undefined"
                       [rounded]="true">
                </p-tag>
              }
            </div>
          }
        </div>

        <div class="tg-header-right">
          <!-- Call button: start / stop. На клике — открывает language-popover,
               если возможен выбор. -->
          @if (!isInSession) {
            <p-button
              icon="pi pi-phone"
              [label]="startButtonLabel() ?? undefined"
              [rounded]="true"
              severity="success"
              [disabled]="!canStartTest"
              [loading]="isLoading"
              [pTooltip]="startButtonTooltip()"
              tooltipPosition="bottom"
              tooltipStyleClass="widget-tooltip"
              (click)="startCallClicked($event, langPop)">
            </p-button>
          } @else {
            <p-button
              icon="pi pi-phone"
              [label]="endButtonLabel() ?? undefined"
              [rounded]="true"
              severity="danger"
              class="icon-slashed"
              [pTooltip]="endButtonTooltip()"
              tooltipPosition="bottom"
              tooltipStyleClass="widget-tooltip"
              (click)="stopTest()">
            </p-button>
          }

          <!-- UI language flag — РУ/EN. В widget-режиме прячем: конечный
               юзер сайта не должен выбирать язык нашей админки, язык
               подхватывается из navigator.language / document.documentElement.lang.
               Явно скрываем в agent-edit'овской вкладке Talk to agent
               (showTranslatePicker=false): язык админки управляется глобально,
               второй селектор внутри тестера лишний. -->
          @if (!isWidgetMode() && showTranslatePicker()) {
            <p-button
              icon="pi pi-language"
              [label]="languageService.getCurrentInfo().flag"
              severity="secondary"
              [text]="true"
              [rounded]="true"
              (click)="toggleUiLanguageMenu($event)">
            </p-button>
            <p-menu #uiLanguageMenu [model]="uiLanguageMenuItems()" [popup]="true" [appendTo]="'body'" [styleClass]="popoverStyleClass()"></p-menu>
          }

          <!-- Popover со списком языков агента (показывается только если их >1) -->
          <p-popover #langPop [styleClass]="popoverStyleClass()">
            <ng-template pTemplate="content">
              <div class="lang-popover">
                <div class="popover-title" i18n="@@agentTest.chooseLanguage">Choose language</div>
                @for (lang of languageOptions; track lang.value) {
                  <button class="lang-row" type="button"
                          [class.is-default]="lang.isDefault"
                          (click)="chooseLanguageAndStart(lang.value, langPop)">
                    <span>{{ lang.label }}</span>
                    @if (lang.isDefault) {
                      <i class="pi pi-star-fill default-star"></i>
                    }
                  </button>
                }
              </div>
            </ng-template>
          </p-popover>
        </div>
      </div>

      <!-- ============================ BODY ============================ -->
      <div class="tg-body" #chatContainer>
        @if (showMicBlockedBanner) {
          <div class="mic-blocked-banner">
            <i class="pi pi-microphone-slash mic-blocked-icon"></i>
            <div class="mic-blocked-text">
              <strong i18n="@@agentTest.micBlocked.title">Microphone access blocked</strong>
              @if (micRequestAttempted) {
                <p i18n="@@agentTest.micBlocked.manualHint">The browser remembered your decision and won't show the permission prompt again. Unblock it manually:</p>
                <ol class="mic-blocked-steps">
                  <li i18n="@@agentTest.micBlocked.step1">Click the lock/settings icon to the left of the address bar.</li>
                  <li i18n="@@agentTest.micBlocked.step2">In "Site permissions" set "Microphone" to Allow.</li>
                  <li i18n="@@agentTest.micBlocked.step3">Come back here and press "I've allowed it — re-check".</li>
                </ol>
              } @else {
                <p i18n="@@agentTest.micBlocked.hint">A voice session can't start without a microphone. Click "Request access" — the browser will show the permission prompt.</p>
              }
            </div>
            <p-button
              [label]="micRequestAttempted ? micBlockedRecheckLabel : micBlockedRequestLabel"
              icon="pi pi-microphone"
              severity="warn"
              (click)="requestMicrophoneAccess()"
              class="w-full">
            </p-button>
          </div>
        } @else if (micErrorText) {
          <div class="inline-banner inline-banner--error">
            <i class="pi pi-exclamation-triangle"></i>
            <span class="inline-banner-text">{{ micErrorText }}</span>
          </div>
        }

        @if (inlineMessage) {
          <div class="inline-banner"
               [class.inline-banner--error]="inlineMessage.type === 'error'"
               [class.inline-banner--warn]="inlineMessage.type === 'warn'"
               [class.inline-banner--info]="inlineMessage.type === 'info'"
               [class.inline-banner--success]="inlineMessage.type === 'success'">
            <i [class]="'pi ' + (
              inlineMessage.type === 'error' ? 'pi-exclamation-triangle' :
              inlineMessage.type === 'warn' ? 'pi-exclamation-circle' :
              inlineMessage.type === 'success' ? 'pi-check-circle' :
              'pi-info-circle'
            )"></i>
            <span class="inline-banner-text">{{ inlineMessage.text }}</span>
            @if (inlineMessage.link) {
              @if (inlineMessage.link.href) {
                <a class="inline-banner-link"
                   [href]="inlineMessage.link.href"
                   target="_blank"
                   rel="noopener">
                  {{ inlineMessage.link.text }}
                  <i class="pi pi-arrow-right"></i>
                </a>
              } @else {
                <a class="inline-banner-link"
                   [routerLink]="inlineMessage.link.commands"
                   [queryParams]="inlineMessage.link.queryParams || null">
                  {{ inlineMessage.link.text }}
                  <i class="pi pi-arrow-right"></i>
                </a>
              }
            }
            <button type="button"
                    class="inline-banner-close"
                    aria-label="Close"
                    (click)="clearInlineMessage()">
              <i class="pi pi-times"></i>
            </button>
          </div>
        }

        @if (variablesFormOpen()) {
          <div class="variables-input">
            <div class="variables-input-title" i18n="@@agentTest.variables.title">Fill in variables</div>
            <p class="variables-input-hint" i18n="@@agentTest.variables.hint">These values are passed to the agent when the call starts.</p>
            <app-properties-edit
              [schemas]="[variableSchemaDict()!]"
              [values]="variableValues()"
              [required]="variableRequiredKeys()"
              (valuesChange)="onVariableValuesChange($event)"
              (validChange)="variablesValid.set($event)">
            </app-properties-edit>
            <div class="variables-input-actions">
              <p-button
                i18n-label="@@agentTest.variables.submit"
                label="Submit"
                icon="pi pi-phone"
                [disabled]="!variablesValid()"
                (click)="submitVariables()">
              </p-button>
            </div>
          </div>
        }

        @if (transcriptVisible()) {
          @if (chatMessages.length > 0) {
            <app-chat-history-view
              [messages]="chatMessages"
              [streamingMessageIds]="streamingMessageIds"
              [assistantName]="agentLabel"
              [showMetrics]="false"
              [showTimestamps]="true">
            </app-chat-history-view>
          } @else if (sessionStatus === 'waiting') {
            <div class="no-messages">
              <i class="pi pi-spin pi-spinner"></i>
              <span i18n="@@agentTest.waiting">Waiting for agent to connect...</span>
            </div>
          } @else if (isConnected) {
            <div class="no-messages">
              <span i18n="@@agentTest.startSpeaking">Start speaking or type a message...</span>
            </div>
          } @else if (!showMicBlockedBanner && !micErrorText && !inlineMessage) {
            <div class="no-messages">
              <i class="pi pi-comments"></i>
              <span i18n="@@agentTest.convPlaceholder">Conversation will appear here</span>
            </div>
          }
        }
      </div>

      <!-- ============================ FOOTER ============================ -->
      <div class="tg-footer">
        <!-- Input-группа всегда в DOM ради плавного transition — CSS
             collapsed-классом сжимает её в max-width: 0 + opacity: 0
             до коннекта, при этом Mic/Speaker естественно сдвигаются
             влево, занимая освободившееся место. На коннекте — обратное:
             группа раскрывается, иконки уезжают вправо. -->
        <div class="tg-input-group" [class.collapsed]="!isConnected" [attr.aria-hidden]="!isConnected">
          <input
            pInputText
            type="text"
            i18n-placeholder="@@agentTest.typeMessage"
            placeholder="Type a message..."
            [(ngModel)]="textMessage"
            (keydown.enter)="sendTextMessage()"
            [disabled]="!isConnected"
            [tabindex]="isConnected ? 0 : -1"
            class="tg-input">
          <p-button
            icon="pi pi-send"
            [rounded]="true"
            severity="primary"
            [disabled]="!isConnected || !textMessage.trim()"
            [tabindex]="isConnected ? 0 : -1"
            (click)="sendTextMessage()">
          </p-button>
        </div>

        <!-- Mic popover trigger -->
        @if (!isObserver()) {
          <p-button
            icon="pi pi-microphone"
            [rounded]="true"
            severity="secondary"
            [text]="true"
            [class.icon-slashed]="isMuted"
            [pTooltip]="isMuted ? micOffTooltip : micOnTooltip"
            tooltipPosition="top"
            tooltipStyleClass="widget-tooltip"
            (click)="micPop.toggle($event)"
            [ariaLabel]="isMuted ? micOffTooltip : micOnTooltip">
          </p-button>
          <p-popover #micPop [styleClass]="popoverStyleClass()" (onShow)="onMicPopoverShow()" (onHide)="onMicPopoverHide()">
            <ng-template pTemplate="content">
              <div class="dev-popover">
                <div class="popover-title" i18n="@@agentTest.microphone">Microphone</div>
                <label class="dev-row">
                  <span class="dev-row-label" i18n="@@common.enabled">Enabled</span>
                  <p-toggleSwitch
                    [ngModel]="!isMuted"
                    (ngModelChange)="toggleMute()">
                  </p-toggleSwitch>
                </label>
                <div class="dev-slider-row">
                  <span class="dev-row-label" i18n="@@agentTest.micLevel">Input level</span>
                  <div class="mic-level-meter">
                    <div class="mic-level-fill" [style.width.%]="micLevel"></div>
                  </div>
                </div>
                <div class="dev-slider-row">
                  <span class="dev-row-label" i18n="@@agentTest.micSensitivity">Sensitivity</span>
                  <p-slider
                    [(ngModel)]="micVolume"
                    [min]="0"
                    [max]="100"
                    [disabled]="isMuted"
                    (onChange)="onMicVolumeChange()">
                  </p-slider>
                </div>
              </div>
            </ng-template>
          </p-popover>
        }

        <!-- Speaker popover trigger -->
        <p-button
          [icon]="speakerOn() ? 'pi pi-volume-up' : 'pi pi-volume-off'"
          [rounded]="true"
          severity="secondary"
          [text]="true"
          [pTooltip]="speakerOn() ? speakerOnTooltip : speakerOffTooltip"
          tooltipPosition="top"
            tooltipStyleClass="widget-tooltip"
          (click)="spkPop.toggle($event)">
        </p-button>
        <p-popover #spkPop [styleClass]="popoverStyleClass()">
          <ng-template pTemplate="content">
            <div class="dev-popover">
              <div class="popover-title" i18n="@@agentTest.speaker">Speaker</div>
              <label class="dev-row">
                <span class="dev-row-label" i18n="@@common.enabled">Enabled</span>
                <p-toggleSwitch
                  [ngModel]="speakerOn()"
                  (ngModelChange)="toggleSpeaker()">
                </p-toggleSwitch>
              </label>
              <div class="dev-slider-row">
                <span class="dev-row-label" i18n="@@agentTest.speakerVolume">Volume</span>
                <p-slider
                  [(ngModel)]="speakerVolume"
                  [min]="0"
                  [max]="100"
                  [disabled]="!speakerOn()"
                  (onChange)="onSpeakerVolumeChange()">
                </p-slider>
              </div>
            </div>
          </ng-template>
        </p-popover>
      </div>

    </div>
    @if (isWidgetMode()) {
      <div class="powered-by">
        <a [href]="appLandingUrl" target="_blank" rel="noopener" i18n="@@agentTest.poweredBy">Powered by {{ appName }}</a>
      </div>
    }
    </div>
  }
</div>
